'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';

import { Eye, EyeOff, Music2, Users, ArrowRight, Zap, Mail, RefreshCw, KeyRound } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { sendVerificationCode } from '@/lib/email';
import { sendPasswordResetEmail } from '@/lib/email';

type AuthMode = 'login' | 'signup' | 'forgot';
type PublicUserRole = 'user' | 'artist';
type SignupStep = 'form' | 'verify';

interface AuthFormData {
  email: string;
  password: string;
  confirmPassword?: string;
  name?: string;
  genre?: string;
  bio?: string;
  role: PublicUserRole;
}

const publicRoleConfig = {
  user: {
    icon: Users,
    label: 'Music Fan',
    description: 'Discover, support campaigns, and grow your momentum wallet',
    color: 'var(--secondary)',
    gradient: 'linear-gradient(135deg, rgba(42,157,143,0.2), rgba(42,157,143,0.05))',
    border: 'rgba(42,157,143,0.5)',
    redirect: '/music-discovery-feed',
  },
  artist: {
    icon: Music2,
    label: 'Artist',
    description: 'Add tracks, launch token campaigns, engage fans via PULSE',
    color: 'var(--accent)',
    gradient: 'linear-gradient(135deg, rgba(59,191,176,0.2), rgba(59,191,176,0.05))',
    border: 'rgba(59,191,176,0.5)',
    redirect: '/artist-dashboard',
  },
};

// Admin redirect map (used only for login routing after DB role check)
const roleRedirectMap: Record<string, string> = {
  user: '/music-discovery-feed',
  artist: '/artist-dashboard',
  admin: '/admin-dashboard',
};

const genres = ['Electronic', 'Hip-Hop', 'Synthwave', 'R&B', 'Indie', 'Ambient', 'Trap', 'Lo-Fi', 'Pop', 'Afrobeats', 'Other'];

function generateCode(): string {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

export default function AuthScreen() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [selectedRole, setSelectedRole] = useState<PublicUserRole>('user');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [signupStep, setSignupStep] = useState<SignupStep>('form');
  const [pendingFormData, setPendingFormData] = useState<AuthFormData | null>(null);
  const [sentCode, setSentCode] = useState('');
  const [enteredCode, setEnteredCode] = useState('');
  const [codeExpiry, setCodeExpiry] = useState<number>(0);
  const [resending, setResending] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const router = useRouter();
  const { signIn, signUp } = useAuth();

  const { register, handleSubmit, setValue, formState: { errors }, watch, reset } = useForm<AuthFormData>({
    defaultValues: { role: 'user' },
  });

  const onSubmit = async (data: AuthFormData) => {
    setIsLoading(true);
    try {
      if (mode === 'signup') {
        if (data.password !== data.confirmPassword) {
          toast.error('Passwords do not match');
          setIsLoading(false);
          return;
        }
        // Send verification code before creating account
        const code = generateCode();
        const expiry = Date.now() + 10 * 60 * 1000; // 10 minutes
        setSentCode(code);
        setCodeExpiry(expiry);
        setPendingFormData(data);
        const name = data.name || data.email.split('@')[0];
        await sendVerificationCode(data.email, name, code);
        setSignupStep('verify');
        toast.success('Verification code sent to your email!');
      } else {
        await signIn(data.email, data.password);
        const supabase = createClient();
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('role')
            .eq('id', user.id)
            .single();
          const role = (profile?.role as string) || (user.user_metadata?.role as string) || 'user';
          const redirect = roleRedirectMap[role] || '/music-discovery-feed';
          toast.success(`Welcome back!`);
          router.push(redirect);
        }
      }
    } catch (error: any) {
      const msg = error?.message || 'Something went wrong';
      if (msg.includes('Invalid login credentials')) {
        toast.error('Invalid email or password. Please check your credentials.');
      } else if (msg.includes('already registered') || msg.includes('already been registered')) {
        toast.error('This email is already registered. Sign in instead.');
        setMode('login');
      } else if (msg.includes('Email not confirmed')) {
        toast.error('Please confirm your email address before signing in.');
      } else {
        toast.error(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!pendingFormData) return;
    if (Date.now() > codeExpiry) {
      toast.error('Verification code has expired. Please request a new one.');
      return;
    }
    if (enteredCode.trim() !== sentCode) {
      toast.error('Incorrect code. Please try again.');
      return;
    }
    setIsLoading(true);
    try {
      await signUp(pendingFormData.email, pendingFormData.password, {
        fullName: pendingFormData.name || '',
        role: selectedRole,
        genre: pendingFormData.genre || '',
        bio: pendingFormData.bio || '',
      });
      toast.success('Account created! Welcome to ECHOMINT 🎵');
      const config = publicRoleConfig[selectedRole];
      router.push(config.redirect);
    } catch (error: any) {
      const msg = error?.message || 'Something went wrong';
      if (msg.includes('already registered') || msg.includes('already been registered')) {
        toast.error('This email is already registered. Sign in instead.');
        setMode('login');
        setSignupStep('form');
      } else {
        toast.error(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!pendingFormData) return;
    setResending(true);
    const code = generateCode();
    const expiry = Date.now() + 10 * 60 * 1000;
    setSentCode(code);
    setCodeExpiry(expiry);
    setEnteredCode('');
    const name = pendingFormData.name || pendingFormData.email.split('@')[0];
    await sendVerificationCode(pendingFormData.email, name, code);
    toast.success('New verification code sent!');
    setResending(false);
  };

  const handleBackToForm = () => {
    setSignupStep('form');
    setPendingFormData(null);
    setSentCode('');
    setEnteredCode('');
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail || !forgotEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setIsLoading(true);
    try {
      const supabase = createClient();
      const resetUrl = `${process.env.NEXT_PUBLIC_SITE_URL}/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: resetUrl,
      });
      if (error) throw error;
      // Send branded ECHOMINT reset email
      const name = forgotEmail.split('@')[0];
      await sendPasswordResetEmail(forgotEmail, name, resetUrl);
      setForgotSent(true);
      toast.success('Password reset link sent to your email!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--background)' }}>
      {/* Left panel */}
      <div className="hidden lg:flex flex-col w-1/2 relative overflow-hidden p-12 hero-grid">
        <div className="absolute top-1/4 left-1/4 w-80 h-80 orb-purple pointer-events-none" />
        <div className="absolute bottom-1/3 right-1/4 w-60 h-60 orb-pink pointer-events-none" />

        <Link href="/" className="flex items-center gap-3 relative z-10">
          <img
            src="/assets/images/logo-removebg-preview_2_-1778277745384.png"
            alt="ECHOMINT Logo"
            style={{ width: 48, height: 48, objectFit: 'contain' }}
          />
        </Link>

        <div className="flex-1 flex flex-col justify-center relative z-10 max-w-md">
          <div className="flex items-center gap-2 mb-6">
            <Zap size={20} style={{ color: 'var(--secondary)' }} />
            <span className="badge-purple text-xs font-semibold px-3 py-1.5 rounded-full">
              Momentum Token Platform
            </span>
          </div>
          <h1 className="text-4xl font-bold mb-4 leading-tight">
            Tokenize Music <span className="gradient-text-cyan">Momentum.</span>
          </h1>
          <p className="text-base leading-relaxed mb-8" style={{ color: 'var(--muted-foreground)' }}>
            Artists create token campaigns. Fans buy units early. As tracks gain momentum, Momentum Value rises. Wallets grow with the movement.
          </p>

          <div className="space-y-3">
            {[
              { num: '12,400+', label: 'Artists on ECHOMINT' },
              { num: '₦2.1B+', label: 'Momentum Value' },
              { num: '340K+', label: 'Token Campaigns' },
            ].map((s) => (
              <div key={`auth-stat-${s.label}`} className="flex items-center gap-3">
                <span className="font-bold tabular-nums gradient-text-cyan">{s.num}</span>
                <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <div className="audio-wave">
            {[1,2,3,4,5,6,7,8,9,10,11,12].map((_, i) => (
              <span key={`auth-wave-${i}`} />
            ))}
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col justify-center px-4 py-8 sm:px-6 sm:py-12 lg:px-12 xl:px-16 overflow-y-auto"
        style={{ background: 'var(--card)' }}>
        <div className="max-w-md w-full mx-auto">
          {/* Mobile logo */}
          <Link href="/" className="flex lg:hidden items-center gap-2 mb-8">
            <img
              src="/assets/images/logo-removebg-preview_2_-1778277745384.png"
              alt="ECHOMINT Logo"
              style={{ width: 36, height: 36, objectFit: 'contain' }}
            />
          </Link>

          {/* Forgot password flow */}
          {mode === 'forgot' && (
            <div>
              <button onClick={() => { setMode('login'); setForgotSent(false); setForgotEmail(''); }}
                className="flex items-center gap-1.5 text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
                ← Back to Sign In
              </button>
              <div className="flex items-center justify-center w-16 h-16 rounded-2xl mx-auto mb-6"
                style={{ background: 'rgba(42,157,143,0.12)', border: '1px solid rgba(42,157,143,0.3)' }}>
                <KeyRound size={28} style={{ color: 'var(--secondary)' }} />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-center">Reset Password</h2>
              {forgotSent ? (
                <div className="text-center space-y-4">
                  <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                    We sent a password reset link to <strong style={{ color: 'var(--foreground)' }}>{forgotEmail}</strong>.
                    Check your inbox and follow the link to set a new password.
                  </p>
                  <button
                    onClick={() => { setMode('login'); setForgotSent(false); setForgotEmail(''); }}
                    className="w-full btn-primary py-3 text-sm font-semibold"
                  >
                    Back to Sign In
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-center mb-6" style={{ color: 'var(--muted-foreground)' }}>
                    Enter your email and we'll send you a reset link.
                  </p>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Email Address</label>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="w-full px-4 py-3 text-sm rounded-lg"
                      style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                    />
                  </div>
                  <button
                    onClick={handleForgotPassword}
                    disabled={isLoading}
                    className="w-full btn-primary py-3 text-sm font-semibold flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    ) : (
                      <>Send Reset Link <ArrowRight size={16} /></>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Email verification step */}
          {mode === 'signup' && signupStep === 'verify' && (
            <div>
              <div className="flex items-center justify-center w-16 h-16 rounded-2xl mx-auto mb-6"
                style={{ background: 'rgba(42,157,143,0.12)', border: '1px solid rgba(42,157,143,0.3)' }}>
                <Mail size={28} style={{ color: 'var(--secondary)' }} />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-center">Check your email</h2>
              <p className="text-sm mb-2 text-center" style={{ color: 'var(--muted-foreground)' }}>
                We sent a 6-digit code to
              </p>
              <p className="text-sm font-semibold mb-8 text-center" style={{ color: 'var(--foreground)' }}>
                {pendingFormData?.email}
              </p>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 text-center">Enter verification code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={enteredCode}
                  onChange={(e) => setEnteredCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full px-4 py-4 text-2xl font-bold rounded-lg text-center tracking-[0.5em]"
                  style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                />
              </div>

              <button
                onClick={handleVerifyCode}
                disabled={isLoading || enteredCode.length !== 6}
                className="w-full btn-primary py-3 text-sm font-semibold flex items-center justify-center gap-2 mb-4"
              >
                {isLoading ? (
                  <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                ) : (
                  <>Verify & Create Account <ArrowRight size={16} /></>
                )}
              </button>

              <div className="flex items-center justify-between">
                <button onClick={handleBackToForm} className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  ← Back
                </button>
                <button
                  onClick={handleResendCode}
                  disabled={resending}
                  className="flex items-center gap-1.5 text-sm font-medium"
                  style={{ color: 'var(--secondary)' }}
                >
                  {resending ? <div className="w-3 h-3 rounded-full border border-current border-t-transparent animate-spin" /> : <RefreshCw size={13} />}
                  Resend code
                </button>
              </div>
            </div>
          )}

          {/* Main login/signup form */}
          {mode !== 'forgot' && !(mode === 'signup' && signupStep === 'verify') && (
            <>
              {/* Mode toggle */}
              <div className="flex rounded-xl p-1 mb-8" style={{ background: 'var(--muted)' }}>
                {(['login', 'signup'] as AuthMode[]).map((m) => (
                  <button
                    key={`mode-${m}`}
                    onClick={() => { setMode(m); reset(); setSignupStep('form'); }}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${mode === m ? 'shadow-sm' : ''}`}
                    style={{
                      background: mode === m ? 'var(--card)' : 'transparent',
                      color: mode === m ? 'var(--foreground)' : 'var(--muted-foreground)',
                    }}
                  >
                    {m === 'login' ? 'Sign In' : 'Create Account'}
                  </button>
                ))}
              </div>

              <h2 className="text-2xl font-bold mb-2">
                {mode === 'login' ? 'Welcome back' : 'Create your account'}
              </h2>
              <p className="text-sm mb-8" style={{ color: 'var(--muted-foreground)' }}>
                {mode === 'login' ? 'Sign in to your ECHOMINT account' : 'Join ECHOMINT and start your journey'}
              </p>

              {/* Role selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-3">
                  {mode === 'signup' ? 'I want to join as' : 'Sign in as'}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {(Object.entries(publicRoleConfig) as [PublicUserRole, typeof publicRoleConfig.user][]).map(([role, config]) => {
                    const RoleIcon = config.icon;
                    const isSelected = selectedRole === role;
                    return (
                      <button
                        key={`role-${role}`}
                        type="button"
                        onClick={() => { setSelectedRole(role); setValue('role', role); }}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl border text-center transition-all duration-200"
                        style={{
                          background: isSelected ? config.gradient : 'var(--muted)',
                          borderColor: isSelected ? config.border : 'var(--border)',
                          boxShadow: isSelected ? `0 0 16px ${config.color}30` : 'none',
                        }}
                      >
                        <RoleIcon size={20} style={{ color: isSelected ? config.color : 'var(--muted-foreground)' }} />
                        <span className="text-xs font-semibold" style={{ color: isSelected ? config.color : 'var(--muted-foreground)' }}>
                          {config.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs mt-2" style={{ color: 'var(--muted-foreground)' }}>
                  {publicRoleConfig[selectedRole].description}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {mode === 'signup' && (
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Full Name</label>
                    <input
                      type="text"
                      placeholder="Your name"
                      className="w-full px-4 py-3 text-sm rounded-lg"
                      style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                      {...register('name', { required: mode === 'signup' ? 'Name is required' : false })}
                    />
                    {errors.name && <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>{errors.name.message}</p>}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1.5">Email</label>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 text-sm rounded-lg"
                    style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                    {...register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' } })}
                  />
                  {errors.email && <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>{errors.email.message}</p>}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium">Password</label>
                    {mode === 'login' && (
                      <button
                        type="button"
                        onClick={() => setMode('forgot')}
                        className="text-xs font-medium"
                        style={{ color: 'var(--secondary)' }}
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={mode === 'signup' ? 'Min 8 characters' : 'Your password'}
                      className="w-full px-4 py-3 pr-11 text-sm rounded-lg"
                      style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                      {...register('password', {
                        required: 'Password is required',
                        minLength: mode === 'signup' ? { value: 8, message: 'Min 8 characters' } : undefined,
                      })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>{errors.password.message}</p>}
                </div>

                {mode === 'signup' && (
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Confirm Password</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Repeat your password"
                      className="w-full px-4 py-3 text-sm rounded-lg"
                      style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                      {...register('confirmPassword', { required: mode === 'signup' ? 'Please confirm password' : false })}
                    />
                    {errors.confirmPassword && <p className="text-xs mt-1" style={{ color: 'var(--negative)' }}>{errors.confirmPassword.message}</p>}
                  </div>
                )}

                {mode === 'signup' && selectedRole === 'artist' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Primary Genre</label>
                      <select
                        className="w-full px-4 py-3 text-sm rounded-lg"
                        style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                        {...register('genre')}
                      >
                        <option value="">Select a genre</option>
                        {genres.map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Bio <span style={{ color: 'var(--muted-foreground)' }}>(optional)</span></label>
                      <textarea
                        placeholder="Tell fans about yourself..."
                        rows={3}
                        className="w-full px-4 py-3 text-sm rounded-lg resize-none"
                        style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                        {...register('bio')}
                      />
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full btn-primary py-3 text-sm font-semibold flex items-center justify-center gap-2 mt-2"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  ) : (
                    <>
                      {mode === 'login' ? 'Sign In' : 'Continue'}
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </form>

              <p className="text-center text-sm mt-6" style={{ color: 'var(--muted-foreground)' }}>
                {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                <button
                  onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); reset(); setSignupStep('form'); }}
                  className="font-semibold"
                  style={{ color: 'var(--secondary)' }}
                >
                  {mode === 'login' ? 'Sign up' : 'Sign in'}
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}